import { LinkBreakIcon, LinkSimpleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import {
  applyRatioLink,
  applyStep,
  captureRatio,
  clampValue,
  parseNumeric,
  roundToStep,
} from './numeric';

import { PROPERTY_LABEL, PROPERTY_ROW, PROPERTY_UNIT, PROPERTY_VALUE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface NumericFieldSpec {
  /** One-letter axis label — `X`, `Y`, `W`, `H`, `R`. */
  label: string;
  value: number;
  /** Rendered as a SEPARATE muted element, never concatenated into the value. */
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  'aria-label'?: string;
}

interface NumericFieldProps extends NumericFieldSpec {
  onChange: (next: number, shift: boolean) => void;
  disabled?: boolean;
}

/**
 * One half of a paired numeric row. Shares the filled property-row recipe so a
 * `X 64 px` cell and a `Font  Inter ▾` row sit on the same visual rail.
 *
 * Keyboard: ↑/↓ steps by `step`, ⇧↑/⇧↓ by ten steps — the convention every
 * design tool shares, so a 10px nudge does not mean holding a key down.
 */
function NumericField({
  label,
  value,
  unit,
  min,
  max,
  step = 1,
  disabled,
  onChange,
  ...rest
}: NumericFieldProps): JSX.Element {
  const [draft, setDraft] = React.useState<string | null>(null);
  const display = draft ?? String(value);

  const commit = (raw: string, shift = false) => {
    const parsed = clampValue(roundToStep(parseNumeric(raw, value), step), min, max);
    setDraft(null);
    if (parsed !== value) onChange(parsed, shift);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      if (event.key === 'Enter') commit(display);
      return;
    }
    event.preventDefault();
    const base = parseNumeric(display, value);
    const next = applyStep(base, {
      step,
      direction: event.key === 'ArrowUp' ? 1 : -1,
      shift: event.shiftKey,
      min,
      max,
    });
    setDraft(null);
    if (next !== value) onChange(next, event.shiftKey);
  };

  return (
    <div
      className={cn(
        PROPERTY_ROW,
        'gap-1.5 px-2 transition-colors focus-within:bg-muted-foreground/10',
        disabled && 'opacity-50',
      )}
    >
      <span className={PROPERTY_LABEL} aria-hidden>
        {label}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline justify-end gap-1">
        <input
          aria-label={rest['aria-label'] ?? label}
          value={display}
          disabled={disabled}
          inputMode="decimal"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(PROPERTY_VALUE, 'w-full min-w-0 bg-transparent outline-none')}
        />
        {unit ? <span className={PROPERTY_UNIT}>{unit}</span> : null}
      </span>
    </div>
  );
}

export interface NumericPairRowProps {
  /** The two axes, left then right — `X`/`Y` or `W`/`H`. */
  fields: readonly [NumericFieldSpec, NumericFieldSpec];
  onChange: (next: [number, number]) => void;
  /**
   * Enables the proportion-constraining link toggle. Present on `W`/`H`, absent
   * on `X`/`Y` — position has no proportion to preserve.
   */
  ratioLock?: {
    locked: boolean;
    onToggle: (locked: boolean) => void;
  };
  disabled?: boolean;
  className?: string;
}

/**
 * The `X 64 px` / `Y 98 px` and `W 425` / `H 144` pattern.
 *
 * WHY (measured, Hightouch): two 108.3×32 cells for Position, two 98.8×32 for
 * Dimensions — narrower precisely to make room for the link toggle. Mora
 * expresses that as `grid-cols-2` vs `grid-cols-[1fr_1fr_auto]` rather than
 * hardcoding two near-identical widths.
 */
export function NumericPairRow({
  fields,
  onChange,
  ratioLock,
  disabled,
  className,
}: NumericPairRowProps): JSX.Element {
  const [a, b] = fields;

  // Frozen at the moment the lock is engaged. Recomputing it from the live pair
  // on every keystroke is the classic drift bug: each rounded write nudges the
  // ratio, and twenty keystrokes later the locked proportion is gone.
  const lockedRatio = React.useRef(captureRatio({ a: a.value, b: b.value }));
  const wasLocked = React.useRef(ratioLock?.locked ?? false);
  if (ratioLock?.locked && !wasLocked.current) {
    lockedRatio.current = captureRatio({ a: a.value, b: b.value });
  }
  wasLocked.current = ratioLock?.locked ?? false;

  const handleAxis = (axis: 'a' | 'b') => (next: number) => {
    if (ratioLock?.locked) {
      const pair = applyRatioLink({
        axis,
        nextValue: next,
        ratio: lockedRatio.current,
        bounds: { aMin: a.min, aMax: a.max, bMin: b.min, bMax: b.max },
        step: a.step ?? 1,
      });
      onChange([pair.a, pair.b]);
      return;
    }
    onChange(axis === 'a' ? [next, b.value] : [a.value, next]);
  };

  return (
    <div
      className={cn(
        'grid gap-1.5',
        ratioLock ? 'grid-cols-[1fr_1fr_auto]' : 'grid-cols-2',
        className,
      )}
    >
      <NumericField {...a} disabled={disabled} onChange={handleAxis('a')} />
      <NumericField {...b} disabled={disabled} onChange={handleAxis('b')} />
      {ratioLock ? (
        <button
          type="button"
          disabled={disabled}
          aria-pressed={ratioLock.locked}
          aria-label={
            ratioLock.locked ? 'Unlock the width-to-height ratio' : 'Lock the width-to-height ratio'
          }
          onClick={() => ratioLock.onToggle(!ratioLock.locked)}
          className={cn(
            'inline-flex size-8 shrink-0 items-center justify-center rounded-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50',
            ratioLock.locked
              ? 'bg-selection-accent/10 text-selection-accent'
              : 'bg-muted text-muted-foreground hover:text-foreground dark:bg-muted/40',
          )}
        >
          {ratioLock.locked ? (
            <LinkSimpleIcon className="size-3.5" />
          ) : (
            <LinkBreakIcon className="size-3.5" />
          )}
        </button>
      ) : null}
    </div>
  );
}
